import type { HubState } from '../state';
import { AppState, FolderIdToHubPersonaIdsMap, getActiveLeftNavItemId } from '../../appstate';
import { HubPersona, createSelector, Person } from '../../shared';
import type { ContactFolder } from './personaListSelector';
import { getPublicFolderForHubPersona } from './personaSelectorUtils';
import { getCurrentHubPersona } from './currentPersonaSelector';
import type { EffectiveRightsType } from '../../service';

export const getPublicFolderForCurrentHubPersona = createSelector<
    AppState,
    HubState,
    HubPersona,
    string,
    ContactFolder[],
    FolderIdToHubPersonaIdsMap,
    ContactFolder
>(
    'getPublicFolderForCurrentHubPersona',
    [
        getCurrentHubPersona,
        getActiveLeftNavItemId,
        (appState: AppState) => appState.data.contactFoldersState.publicFolders,
        (appState: AppState) => appState.data.contactFoldersState.folderIdToHubPersonaIds,
    ],
    (currentHubPersona, activeLeftnavItemId, publicFolders, folderIdToHubPersonaIdsMap) => {
        return getPublicFolderForHubPersona(
            currentHubPersona,
            activeLeftnavItemId,
            publicFolders,
            folderIdToHubPersonaIdsMap
        );
    }
);

export const isCurrentHubPersonaInPublicFolder = createSelector<
    AppState,
    HubState,
    ContactFolder,
    boolean
>(
    'isCurrentHubPersonaInPublicFolder',
    [getPublicFolderForCurrentHubPersona],
    folderId => !!folderId
);

export const getPublicFolderEffectiveRightsForCurrentHubPersona = createSelector<
    AppState,
    HubState,
    ContactFolder,
    EffectiveRightsType
>(
    'getPublicFolderEffectiveRightsForCurrentHubPersona',
    [getPublicFolderForCurrentHubPersona],
    publicFolder => {
        return publicFolder?.publicFolderInfo?.effectiveRights;
    }
);

export const isPublicFolderCanDeleteForCurrentHubPersona = createSelector<
    AppState,
    HubState,
    HubPersona,
    boolean
>(
    'isPublicFolderCanDeleteForCurrentHubPersona',
    [getCurrentHubPersona],
    currentHubPersona => (currentHubPersona as Person)?.canDeleteContact === true
);

export const isPublicFolderCanModifyForCurrentHubPersona = createSelector<
    AppState,
    HubState,
    HubPersona,
    boolean
>(
    'isPublicFolderCanModifyForCurrentHubPersona',
    [getCurrentHubPersona],
    currentHubPersona => (currentHubPersona as Person)?.canEditContact === true
);

export const isPublicFolderCanCreateForCurrentHubPersona = createSelector<
    AppState,
    HubState,
    EffectiveRightsType,
    boolean
>(
    'isPublicFolderCanCreateForCurrentHubPersona',
    [getPublicFolderEffectiveRightsForCurrentHubPersona],
    effectiveRights => effectiveRights && effectiveRights.CreateContents === true
);

export const isPublicFolderCanReadForCurrentHubPersona = createSelector<
    AppState,
    HubState,
    EffectiveRightsType,
    boolean
>(
    'isPublicFolderCanReadForCurrentHubPersona',
    [getPublicFolderEffectiveRightsForCurrentHubPersona],
    effectiveRights => effectiveRights && effectiveRights.Read === true
);
